import { localeFormatter } from '@/i18n/format'

import type { DisplayCurrency } from './map'

export interface FormatCurrencyOptions {
    /**
     * Minor-unit digits to render. Omit to take the currency's own CLDR default (2 for
     * both USD and MXN). This is the **requesting surface's** choice (spec FR-8): the
     * average fare renders with cents, a "from" fare whole — and that choice is the same
     * whether or not the amount was converted.
     */
    fractionDigits?: number
}

/**
 * Render an amount by the conventions of its currency in the active locale.
 *
 * Deliberately locale-and-currency driven rather than symbol-driven: `USD` in `en-us` and
 * `MXN` in `es-mx` both come out with a bare `$` (spec RD-5), which is the point — the same
 * fare must read the same way here as on the other Wanderu surfaces. A reader therefore
 * cannot tell the currency from the glyph; where it must be unambiguous it travels as an
 * ISO code (FR-11), never inferred from the rendered string.
 *
 * **This is the app's only price formatter.** Every rendered fare resolves here — the
 * components, the transforms that format prices into copy, and the chart axes alike — so
 * one page cannot state two of its prices by different conventions. Formatted through the
 * shared next-intl formatter (`@/i18n/format`) rather than a local `Intl.NumberFormat`, so
 * a price and a date on the same page go through one locale-resolution and one Intl cache.
 */
export function formatCurrency(
    amount: number,
    currency: DisplayCurrency,
    locale: string,
    { fractionDigits }: FormatCurrencyOptions = {},
): string {
    return localeFormatter(locale).number(amount, {
        style: 'currency',
        currency,
        ...(fractionDigits === undefined
            ? {}
            : { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }),
    })
}
